/**
 * System parameters — the reference tables behind the Parameters screens and
 * most of the application's dropdowns.
 *
 * The Parameters pages are schema-driven: they ask for a table's schema, then
 * render a list and a form from it. The sample layer therefore has to supply
 * both a schema and rows for every table a tour visits (Company, Security,
 * PlanType, Plan, …) rather than just a list of values.
 */

import type { ParameterTable } from '@/services/types';
import type {
  ParameterSchema,
  ParameterSchemaField,
} from '@/services/types/parameter-types';

type FieldSeed = Pick<ParameterSchemaField, 'fieldName' | 'displayName'> &
  Partial<ParameterSchemaField>;

/** Fill a terse field seed out into the full schema field the forms expect. */
const field = (seed: FieldSeed, order: number): ParameterSchemaField => ({
  fieldId: 1000 + order,
  fieldType: 'String',
  isRequired: false,
  isUnique: false,
  isUniqueIdentifier: false,
  maxLength: 120,
  minValue: null,
  maxValue: null,
  regexPattern: null,
  validationMessage: null,
  defaultValue: null,
  placeholder: null,
  helpText: null,
  displayOrder: order,
  isSystemField: false,
  isEditable: true,
  isVisibleInList: true,
  showInMaintenanceList: true,
  isVisibleInForm: true,
  dataSource: null,
  valueField: null,
  displayField: null,
  filterCondition: null,
  ...seed,
});

/** The status column every parameter table carries. */
const STATUS_FIELD: FieldSeed = {
  fieldName: 'status',
  displayName: 'Status',
  fieldType: 'Dropdown',
  options: ['Active', 'Inactive'],
  isRequired: true,
};

interface TableSeed {
  tableName: string;
  displayName: string;
  fields: FieldSeed[];
  rows: Record<string, any>[];
}

const TABLE_SEEDS: TableSeed[] = [
  {
    tableName: 'Company',
    displayName: 'Company',
    fields: [
      {
        fieldName: 'companyCode',
        displayName: 'Company Code',
        isRequired: true,
        isUnique: true,
        isUniqueIdentifier: true,
        maxLength: 20,
      },
      {
        fieldName: 'companyName',
        displayName: 'Company Name',
        isRequired: true,
      },
      { fieldName: 'country', displayName: 'Country', fieldType: 'Dropdown' },
      { fieldName: 'currency', displayName: 'Currency', fieldType: 'Dropdown' },
      {
        fieldName: 'incorporationDate',
        displayName: 'Incorporation Date',
        fieldType: 'Date',
      },
      STATUS_FIELD,
    ],
    rows: [
      {
        id: 1,
        companyCode: 'NWH-SG',
        companyName: 'Northwind Holdings Pte Ltd',
        country: 'Singapore',
        currency: 'SGD',
        incorporationDate: '2005-06-14',
        status: 'Active',
      },
      {
        id: 2,
        companyCode: 'NWC-MY',
        companyName: 'Northwind Capital Sdn Bhd',
        country: 'Malaysia',
        currency: 'MYR',
        incorporationDate: '2011-02-08',
        status: 'Active',
      },
      {
        id: 3,
        companyCode: 'NWT-HK',
        companyName: 'Northwind Technologies Ltd',
        country: 'Hong Kong',
        currency: 'HKD',
        incorporationDate: '2014-09-30',
        status: 'Active',
      },
      {
        id: 4,
        companyCode: 'NWV-VN',
        companyName: 'Northwind Ventures Co Ltd',
        country: 'Vietnam',
        currency: 'VND',
        incorporationDate: '2019-11-22',
        status: 'Inactive',
      },
    ],
  },
  {
    tableName: 'Security',
    displayName: 'Security',
    fields: [
      {
        fieldName: 'securityCode',
        displayName: 'Security Code',
        isRequired: true,
        isUnique: true,
        isUniqueIdentifier: true,
        maxLength: 20,
      },
      {
        fieldName: 'securityName',
        displayName: 'Security Name',
        isRequired: true,
      },
      {
        fieldName: 'securityType',
        displayName: 'Security Type',
        fieldType: 'Dropdown',
      },
      { fieldName: 'exchange', displayName: 'Exchange' },
      { fieldName: 'currency', displayName: 'Currency', fieldType: 'Dropdown' },
      { fieldName: 'parValue', displayName: 'Par Value', fieldType: 'decimal' },
      STATUS_FIELD,
    ],
    rows: [
      {
        id: 1,
        securityCode: 'NWH-ORD',
        securityName: 'Northwind Holdings Ordinary Shares',
        securityType: 'Ordinary Share',
        exchange: 'SGX',
        currency: 'SGD',
        parValue: 0.1,
        status: 'Active',
      },
      {
        id: 2,
        securityCode: 'NWH-PREF',
        securityName: 'Northwind Holdings Preference Shares',
        securityType: 'Preference Share',
        exchange: 'SGX',
        currency: 'SGD',
        parValue: 1,
        status: 'Active',
      },
      {
        id: 3,
        securityCode: 'NWT-ORD',
        securityName: 'Northwind Technologies Ordinary Shares',
        securityType: 'Ordinary Share',
        exchange: 'HKEX',
        currency: 'HKD',
        parValue: 0.05,
        status: 'Inactive',
      },
    ],
  },
  {
    tableName: 'PlanType',
    displayName: 'Plan Type',
    fields: [
      {
        fieldName: 'planTypeCode',
        displayName: 'Plan Type Code',
        isRequired: true,
        isUnique: true,
        isUniqueIdentifier: true,
        maxLength: 10,
      },
      { fieldName: 'name', displayName: 'Plan Type Name', isRequired: true },
      {
        fieldName: 'settlementType',
        displayName: 'Settlement Type',
        fieldType: 'Dropdown',
      },
      {
        fieldName: 'isPerformanceBased',
        displayName: 'Performance Based',
        fieldType: 'Boolean',
      },
      STATUS_FIELD,
    ],
    rows: [
      {
        id: 301,
        planTypeCode: 'PSP',
        name: 'Performance Share Plan',
        settlementType: 'Equity',
        isPerformanceBased: true,
        status: 'Active',
      },
      {
        id: 302,
        planTypeCode: 'RSP',
        name: 'Restricted Share Plan',
        settlementType: 'Equity',
        isPerformanceBased: false,
        status: 'Active',
      },
      {
        id: 303,
        planTypeCode: 'ESOS',
        name: 'Employee Share Option Scheme',
        settlementType: 'Equity',
        isPerformanceBased: false,
        status: 'Active',
      },
      {
        id: 304,
        planTypeCode: 'PCP',
        name: 'Phantom Cash Plan',
        settlementType: 'Cash',
        isPerformanceBased: true,
        status: 'Inactive',
      },
    ],
  },
  {
    tableName: 'Plan',
    displayName: 'Plan',
    fields: [
      {
        fieldName: 'planCode',
        displayName: 'Plan Code',
        isRequired: true,
        isUnique: true,
        isUniqueIdentifier: true,
        maxLength: 20,
      },
      { fieldName: 'name', displayName: 'Plan Name', isRequired: true },
      {
        fieldName: 'planType',
        displayName: 'Plan Type',
        fieldType: 'Dropdown',
      },
      { fieldName: 'security', displayName: 'Security', fieldType: 'Dropdown' },
      { fieldName: 'startDate', displayName: 'Start Date', fieldType: 'Date' },
      { fieldName: 'endDate', displayName: 'End Date', fieldType: 'Date' },
      {
        fieldName: 'sharesAuthorised',
        displayName: 'Shares Authorised',
        fieldType: 'Number',
      },
      STATUS_FIELD,
    ],
    rows: [
      {
        id: 501,
        planCode: 'NWH-PSP-2025',
        name: 'Northwind Performance Share Plan 2025',
        planType: 'Performance Share Plan',
        security: 'Northwind Holdings Ordinary Shares',
        startDate: '2025-01-01',
        endDate: '2027-12-31',
        sharesAuthorised: 2500000,
        status: 'Active',
      },
      {
        id: 502,
        planCode: 'NWH-RSP-2025',
        name: 'Northwind Restricted Share Plan 2025',
        planType: 'Restricted Share Plan',
        security: 'Northwind Holdings Ordinary Shares',
        startDate: '2025-01-01',
        endDate: '2028-12-31',
        sharesAuthorised: 1200000,
        status: 'Active',
      },
      {
        id: 503,
        planCode: 'NWH-ESOS-2024',
        name: 'Northwind Employee Share Option Scheme 2024',
        planType: 'Employee Share Option Scheme',
        security: 'Northwind Holdings Ordinary Shares',
        startDate: '2024-01-01',
        endDate: '2029-12-31',
        sharesAuthorised: 800000,
        status: 'Active',
      },
    ],
  },
  {
    tableName: 'Department',
    displayName: 'Department',
    fields: [
      {
        fieldName: 'departmentCode',
        displayName: 'Department Code',
        isRequired: true,
        isUniqueIdentifier: true,
        maxLength: 10,
      },
      { fieldName: 'name', displayName: 'Department Name', isRequired: true },
      STATUS_FIELD,
    ],
    rows: [
      { id: 201, departmentCode: 'FIN', name: 'Finance', status: 'Active' },
      {
        id: 202,
        departmentCode: 'HR',
        name: 'Human Resources',
        status: 'Active',
      },
      { id: 203, departmentCode: 'TECH', name: 'Technology', status: 'Active' },
      {
        id: 204,
        departmentCode: 'LEGAL',
        name: 'Legal & Compliance',
        status: 'Active',
      },
      { id: 205, departmentCode: 'OPS', name: 'Operations', status: 'Active' },
      { id: 206, departmentCode: 'SALES', name: 'Sales', status: 'Active' },
    ],
  },
  {
    tableName: 'Country',
    displayName: 'Country',
    fields: [
      {
        fieldName: 'countryCode',
        displayName: 'Country Code',
        isRequired: true,
        isUniqueIdentifier: true,
        maxLength: 3,
      },
      { fieldName: 'name', displayName: 'Country Name', isRequired: true },
      { fieldName: 'currency', displayName: 'Currency' },
      STATUS_FIELD,
    ],
    rows: [
      {
        id: 101,
        countryCode: 'SGP',
        name: 'Singapore',
        currency: 'SGD',
        status: 'Active',
      },
      {
        id: 102,
        countryCode: 'MYS',
        name: 'Malaysia',
        currency: 'MYR',
        status: 'Active',
      },
      {
        id: 103,
        countryCode: 'HKG',
        name: 'Hong Kong',
        currency: 'HKD',
        status: 'Active',
      },
      {
        id: 104,
        countryCode: 'VNM',
        name: 'Vietnam',
        currency: 'VND',
        status: 'Active',
      },
      {
        id: 105,
        countryCode: 'IND',
        name: 'India',
        currency: 'INR',
        status: 'Active',
      },
    ],
  },
  {
    tableName: 'Designation',
    displayName: 'Designation',
    fields: [
      {
        fieldName: 'designationCode',
        displayName: 'Code',
        isUniqueIdentifier: true,
        maxLength: 10,
      },
      { fieldName: 'name', displayName: 'Designation', isRequired: true },
      STATUS_FIELD,
    ],
    rows: [
      {
        id: 601,
        designationCode: 'FM',
        name: 'Finance Manager',
        status: 'Active',
      },
      {
        id: 602,
        designationCode: 'HRBP',
        name: 'HR Business Partner',
        status: 'Active',
      },
      {
        id: 603,
        designationCode: 'EL',
        name: 'Engineering Lead',
        status: 'Active',
      },
      {
        id: 604,
        designationCode: 'CO',
        name: 'Compliance Officer',
        status: 'Active',
      },
      {
        id: 605,
        designationCode: 'OD',
        name: 'Operations Director',
        status: 'Active',
      },
    ],
  },
  {
    tableName: 'JobGrade',
    displayName: 'Job Grade',
    fields: [
      {
        fieldName: 'gradeCode',
        displayName: 'Grade Code',
        isUniqueIdentifier: true,
        maxLength: 10,
      },
      { fieldName: 'name', displayName: 'Grade Name', isRequired: true },
      {
        fieldName: 'isKeyManagement',
        displayName: 'Key Management',
        fieldType: 'Boolean',
      },
      STATUS_FIELD,
    ],
    rows: [
      {
        id: 701,
        gradeCode: 'M2',
        name: 'Manager',
        isKeyManagement: false,
        status: 'Active',
      },
      {
        id: 702,
        gradeCode: 'M3',
        name: 'Senior Manager',
        isKeyManagement: false,
        status: 'Active',
      },
      {
        id: 703,
        gradeCode: 'M4',
        name: 'Director',
        isKeyManagement: true,
        status: 'Active',
      },
      {
        id: 704,
        gradeCode: 'M5',
        name: 'Managing Director',
        isKeyManagement: true,
        status: 'Active',
      },
    ],
  },
  {
    tableName: 'VestingSchedule',
    displayName: 'Vesting Schedule',
    fields: [
      {
        fieldName: 'scheduleCode',
        displayName: 'Code',
        isUniqueIdentifier: true,
        maxLength: 20,
      },
      { fieldName: 'name', displayName: 'Schedule Name', isRequired: true },
      {
        fieldName: 'numberOfTranches',
        displayName: 'Tranches',
        fieldType: 'Number',
      },
      { fieldName: 'vestingPattern', displayName: 'Pattern' },
      STATUS_FIELD,
    ],
    rows: [
      {
        id: 801,
        scheduleCode: 'GRAD-3Y',
        name: '3-Year Graded (33/33/34)',
        numberOfTranches: 3,
        vestingPattern: 'Graded',
        status: 'Active',
      },
      {
        id: 802,
        scheduleCode: 'CLIFF-3Y',
        name: '3-Year Cliff',
        numberOfTranches: 1,
        vestingPattern: 'Cliff',
        status: 'Active',
      },
      {
        id: 803,
        scheduleCode: 'GRAD-4Y',
        name: '4-Year Graded (25/25/25/25)',
        numberOfTranches: 4,
        vestingPattern: 'Graded',
        status: 'Active',
      },
    ],
  },
  {
    tableName: 'PerformanceMeasure',
    displayName: 'Performance Measure',
    fields: [
      {
        fieldName: 'measureCode',
        displayName: 'Code',
        isUniqueIdentifier: true,
        maxLength: 10,
      },
      { fieldName: 'name', displayName: 'Measure', isRequired: true },
      { fieldName: 'unit', displayName: 'Unit' },
      STATUS_FIELD,
    ],
    rows: [
      {
        id: 401,
        measureCode: 'TSR',
        name: 'Total Shareholder Return',
        unit: '%',
        status: 'Active',
      },
      {
        id: 402,
        measureCode: 'ROE',
        name: 'Return on Equity',
        unit: '%',
        status: 'Active',
      },
      {
        id: 403,
        measureCode: 'EPS',
        name: 'Earnings Per Share Growth',
        unit: '%',
        status: 'Active',
      },
      {
        id: 404,
        measureCode: 'ESG',
        name: 'ESG Scorecard',
        unit: 'Points',
        status: 'Active',
      },
    ],
  },
  {
    tableName: 'CostCenter',
    displayName: 'Cost Center',
    fields: [
      {
        fieldName: 'costCenterCode',
        displayName: 'Code',
        isUniqueIdentifier: true,
        maxLength: 10,
      },
      { fieldName: 'name', displayName: 'Cost Center', isRequired: true },
      STATUS_FIELD,
    ],
    rows: [
      {
        id: 901,
        costCenterCode: 'CC-100',
        name: 'Corporate Finance',
        status: 'Active',
      },
      {
        id: 902,
        costCenterCode: 'CC-200',
        name: 'Group Technology',
        status: 'Active',
      },
      {
        id: 903,
        costCenterCode: 'CC-300',
        name: 'Regional Operations',
        status: 'Active',
      },
    ],
  },
  {
    tableName: 'TerminationReason',
    displayName: 'Termination Reason',
    fields: [
      {
        fieldName: 'reasonCode',
        displayName: 'Code',
        isUniqueIdentifier: true,
        maxLength: 10,
      },
      { fieldName: 'name', displayName: 'Reason', isRequired: true },
      {
        fieldName: 'isGoodLeaver',
        displayName: 'Good Leaver',
        fieldType: 'Boolean',
      },
      STATUS_FIELD,
    ],
    rows: [
      {
        id: 1101,
        reasonCode: 'RES',
        name: 'Resignation',
        isGoodLeaver: false,
        status: 'Active',
      },
      {
        id: 1102,
        reasonCode: 'RET',
        name: 'Retirement',
        isGoodLeaver: true,
        status: 'Active',
      },
      {
        id: 1103,
        reasonCode: 'RED',
        name: 'Redundancy',
        isGoodLeaver: true,
        status: 'Active',
      },
    ],
  },
  {
    tableName: 'DepositoryAgent',
    displayName: 'Depository Agent',
    fields: [
      {
        fieldName: 'agentCode',
        displayName: 'Code',
        isUniqueIdentifier: true,
        maxLength: 10,
      },
      { fieldName: 'name', displayName: 'Depository Agent', isRequired: true },
      { fieldName: 'accountNumber', displayName: 'Account Number' },
      STATUS_FIELD,
    ],
    rows: [
      {
        id: 1201,
        agentCode: 'CDP',
        name: 'Central Depository (Pte) Limited',
        accountNumber: '168-000-123456',
        status: 'Active',
      },
      {
        id: 1202,
        agentCode: 'NOM',
        name: 'Northwind Nominees Pte Ltd',
        accountNumber: '168-000-654321',
        status: 'Active',
      },
    ],
  },
  {
    tableName: 'PlanOffering',
    displayName: 'Plan Offering',
    fields: [
      {
        fieldName: 'offeringCode',
        displayName: 'Code',
        isUniqueIdentifier: true,
        maxLength: 20,
      },
      { fieldName: 'name', displayName: 'Offering', isRequired: true },
      { fieldName: 'offeringYear', displayName: 'Year', fieldType: 'Number' },
      STATUS_FIELD,
    ],
    rows: [
      {
        id: 1301,
        offeringCode: 'PSP-2025-01',
        name: 'PSP 2025 Annual Grant',
        offeringYear: 2025,
        status: 'Active',
      },
      {
        id: 1302,
        offeringCode: 'RSP-2025-01',
        name: 'RSP 2025 Annual Grant',
        offeringYear: 2025,
        status: 'Active',
      },
    ],
  },
  {
    tableName: 'KeyManagementGrade',
    displayName: 'Key Management Grade',
    fields: [
      {
        fieldName: 'gradeCode',
        displayName: 'Code',
        isUniqueIdentifier: true,
        maxLength: 10,
      },
      { fieldName: 'name', displayName: 'Grade', isRequired: true },
      STATUS_FIELD,
    ],
    rows: [
      {
        id: 1401,
        gradeCode: 'KM1',
        name: 'Board of Directors',
        status: 'Active',
      },
      {
        id: 1402,
        gradeCode: 'KM2',
        name: 'Executive Committee',
        status: 'Active',
      },
    ],
  },
];

/** Every parameter table the sample layer knows, in Help Center order. */
export const TOUR_PARAMETER_TABLES: ParameterTable[] = TABLE_SEEDS.map(
  (seed, index) => ({
    id: index + 1,
    name: seed.displayName,
    tableName: seed.tableName,
    displayName: seed.displayName,
  })
);

/** Tables offered as a data-access level when building a user group. */
export const TOUR_DATA_ACCESS_LEVEL_TABLES: ParameterTable[] =
  TOUR_PARAMETER_TABLES.filter(table =>
    ['Company', 'Department', 'Country', 'CostCenter'].includes(table.tableName)
  );

const seedByTable = new Map(
  TABLE_SEEDS.map(seed => [seed.tableName.toLowerCase(), seed])
);

const schemaCache = new Map<string, ParameterSchema>();

/**
 * The schema for a parameter table. Unknown tables get a minimal
 * code/name/status schema so a tour that reaches an unfixtured parameter page
 * still renders a working screen instead of an error.
 */
export const getTourParameterSchema = (tableName: string): ParameterSchema => {
  const key = tableName.toLowerCase();
  const cached = schemaCache.get(key);
  if (cached) return cached;

  const seed = seedByTable.get(key);
  const fields = seed?.fields ?? [
    { fieldName: 'code', displayName: 'Code', isUniqueIdentifier: true },
    { fieldName: 'name', displayName: 'Name', isRequired: true },
    STATUS_FIELD,
  ];

  const schema: ParameterSchema = {
    parameterId:
      TOUR_PARAMETER_TABLES.findIndex(
        table => table.tableName.toLowerCase() === key
      ) + 1,
    tableName: seed?.tableName ?? tableName,
    displayName: seed?.displayName ?? tableName,
    fields: fields.map((seedField, index) => field(seedField, index + 1)),
  };

  schemaCache.set(key, schema);
  return schema;
};

/** The rows for a parameter table, with the audit columns the lists show. */
export const getTourParameterRows = (
  tableName: string
): Record<string, any>[] => {
  const seed = seedByTable.get(tableName.toLowerCase());
  if (!seed) return [];
  return seed.rows.map(row => ({
    ...row,
    statusName: row.status,
    createdBy: 'Sarah Lim',
    createdAt: '2024-01-12T09:00:00Z',
    updatedBy: 'Arun Kumar',
    updatedAt: '2025-02-18T13:20:00Z',
  }));
};

/**
 * The `{ table: items }` bundle the dropdown loader asks for. Values keep both
 * `id` and `name` because the option helpers read either.
 */
export const getTourParameterValues = (
  tableNames: string[]
): Record<string, Record<string, any>[]> => {
  const result: Record<string, Record<string, any>[]> = {};
  tableNames.forEach(tableName => {
    const rows = getTourParameterRows(tableName);
    result[tableName] = rows
      .filter(row => row.status === 'Active')
      .map(row => ({
        ...row,
        name: row.name ?? row.companyName ?? row.securityName ?? row.planName,
      }));
  });
  return result;
};

/** Per-table record counts for the Parameters landing cards. */
export const getTourParameterCounts = (): Record<string, number> =>
  Object.fromEntries(
    TABLE_SEEDS.map(seed => [seed.tableName, seed.rows.length])
  );
